import { makeAutoObservable, runInAction } from "mobx";
import i18n from "i18next";
import MainStore from "MainStore";
import { changePassword } from "../../api/Auth/useAuth";
import { validate, validateFieldPassword, validateFieldUserName, validateFieldEmail, validateFieldPhone, validatePassword } from "./valid";
import { getMyProfile, updateUserProfile } from "api/user_accounts";
import { CountryPhone } from "constants/countrieCodes";

class NewStore {
  // Поля для смены пароля
  CurrentPassword = "";
  NewPassword = "";
  ConfirmPassword = "";

  // Поля профиля пользователя
  username = "";
  email = "";
  full_name = "";
  phone = "";
  selectedCountry: CountryPhone | null = null;

  // Состояние валидации
  errors: { [key: string]: string } = {};
  touched: { [key: string]: boolean } = {};

  // Функция навигации
  navigate: (path: string) => void = () => { };

  constructor() {
    makeAutoObservable(this);
  }

  setNavigateFunction(navigate: (path: string) => void) {
    this.navigate = navigate;
  }

  // Получение профиля текущего пользователя
  getMyProfile = async () => {
    try {
      MainStore.changeLoader(true);
      const response = await getMyProfile();

      if ((response.status === 201 || response.status === 200) && response?.data !== null) {
        runInAction(() => {
          this.username = response.data.username || "";
          this.email = response.data.email || "";
          this.full_name = response.data.full_name || "";
          this.phone = response.data.phone || "";

          // Очищаем ошибки и touched при загрузке данных
          this.errors = {};
          this.touched = {};
        });
      } else {
        throw new Error();
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  };

  // Обработчик изменения полей формы
  handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;

    runInAction(() => {
      (this as any)[name] = value;
      this.touched[name] = true;
    });

    this.validateField(name, value);
  }

  // Обработчик изменения телефона
  handlePhoneChange(value: string, country: CountryPhone) {
    runInAction(() => {
      this.phone = value;
      this.selectedCountry = country;
      this.touched.phone = true;
    });

    this.validateField("phone", value);
  }

  // Обработчик потери фокуса
  handleBlur(name: string) {
    runInAction(() => {
      this.touched[name] = true;
    });

    const value = (this as any)[name];
    this.validateField(name, value);
  }

  // Универсальный метод валидации поля
  async validateField(name: string, value: any) {
    if (!this.touched[name]) return;

    if (name === "username" || name === "full_name") {
      await this.validateFieldUserName(name, value);
    } else if (name === "email") {
      await this.validateFieldEmail(name, value);
    } else if (name === "phone") {
      await this.validateFieldPhone(name, value);
    } else if (name === "CurrentPassword" || name === "NewPassword" || name === "ConfirmPassword") {
      await this.validateFieldPassword(name, value);
    }
  }

  async validateFieldUserName(name: string, value: any) {
    const { isValid, error } = await validateFieldUserName(name, value);
    runInAction(() => {
      if (isValid) {
        this.errors[name] = "";
      } else {
        this.errors[name] = error;
      }
    });
  }

  async validateFieldEmail(name: string, value: any) {
    const { isValid, error } = await validateFieldEmail(name, value);
    runInAction(() => {
      if (isValid) {
        this.errors[name] = "";
      } else {
        this.errors[name] = error;
      }
    });
  }

  async validateFieldPhone(name: string, value: any) {
    const { isValid, error } = await validateFieldPhone(name, value);
    runInAction(() => {
      if (isValid) {
        this.errors[name] = "";
      } else {
        this.errors[name] = error;
      }
    });
  }

  async validateFieldPassword(name: string, value: any) {
    // Собираем все данные паролей для кросс-валидации
    const allPasswordData = {
      CurrentPassword: this.CurrentPassword,
      NewPassword: this.NewPassword,
      ConfirmPassword: this.ConfirmPassword,
    };

    const { isValid, error } = await validateFieldPassword(name, value, allPasswordData);

    runInAction(() => {
      if (isValid) {
        this.errors[name] = "";

        // Если меняли NewPassword и есть ConfirmPassword, перепроверяем соответствие
        if (name === 'NewPassword' && this.ConfirmPassword && this.touched.ConfirmPassword) {
          this.validateField('ConfirmPassword', this.ConfirmPassword);
        }
      } else {
        this.errors[name] = error;
      }
    });
  }

  // Метод для валидации всех полей профиля
  markAllProfileFieldsAsTouched() {
    runInAction(() => {
      this.touched.username = true;
      this.touched.full_name = true;
      this.touched.email = true;
      this.touched.phone = true;
    });
  }

  // Обновление профиля пользователя
  onUpdateProfile = async () => {
    try {
      const data = {
        username: this.username,
        email: this.email,
        full_name: this.full_name,
        phone: this.phone,
      };

      this.markAllProfileFieldsAsTouched();

      const { isValid, errors } = await validate(data);
      if (!isValid) {
        runInAction(() => {
          this.errors = errors;
        });
        MainStore.openErrorDialog(i18n.t("message:error.alertMessageAlert"));
        return;
      }

      MainStore.changeLoader(true);
      const response = await updateUserProfile(data);

      if (response.status === 201 || response.status === 200) {
        MainStore.setSnackbar(i18n.t("message:snackbar.successSave"), "success");

        // Обновляем username в localStorage если он изменился
        if (this.username) {
          localStorage.setItem("currentUser", this.username);
        }

        // Обновляем данные пользователя в MainStore
        await MainStore.getCurrentUser();
      } else {
        throw new Error();
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:snackbar.errorEdit"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  };

  // Смена пароля с уведомлением вместо автоматического выхода
  onChangePassword = async () => {
    try {
      const data = {
        Username: localStorage.getItem("currentUser"),
        CurrentPassword: this.CurrentPassword,
        NewPassword: this.NewPassword,
      };

      runInAction(() => {
        this.touched.CurrentPassword = true;
        this.touched.NewPassword = true;
        this.touched.ConfirmPassword = true;
      });

      const dataValidate = { ...data, ConfirmPassword: this.ConfirmPassword };
      const { isValid, errors } = await validatePassword(dataValidate);

      if (!isValid) {
        runInAction(() => {
          this.errors = { ...this.errors, ...errors };
        });
        MainStore.openErrorDialog(i18n.t("message:error.alertMessageAlert"));
        return;
      }

      MainStore.changeLoader(true);
      const response = await changePassword(data);

      if (response.status === 201 || response.status === 200) {
        if (response.data) {
          // Очищаем поля паролей
          this.clearPasswordFields();
          // localStorage.removeItem("token");
          // localStorage.removeItem("currentUser");

          // Показываем сообщение об успешной смене пароля
          MainStore.setSnackbar(
            i18n.t("message:snackbar.passwordChangedSuccess") ||
            "Пароль успешно изменен. Вам нужно перезайти в систему чтобы получить доступ к программе",
            "success"
          );
        } else {
          MainStore.setSnackbar(i18n.t("message:snackbar.errorEdit"), "error");
        }
      } else {
        throw new Error();
      }
    } catch (err) {
      MainStore.setSnackbar(i18n.t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  };

  // Очищаем только поля паролей
  clearPasswordFields = () => {
    runInAction(() => {
      this.CurrentPassword = "";
      this.NewPassword = "";
      this.ConfirmPassword = "";
      delete this.errors.CurrentPassword;
      delete this.errors.NewPassword;
      delete this.errors.ConfirmPassword;
      delete this.touched.CurrentPassword;
      delete this.touched.NewPassword;
      delete this.touched.ConfirmPassword;
    });
  };

  // Полная очистка store
  clearStore = () => {
    runInAction(() => {
      this.CurrentPassword = "";
      this.NewPassword = "";
      this.ConfirmPassword = "";
      this.username = "";
      this.email = "";
      this.full_name = "";
      this.phone = "";
      this.selectedCountry = null;
      this.errors = {};
      this.touched = {};
    });
  };
}

export default new NewStore();